import { useCallback, useState } from 'react';

import retry from 'async-await-retry';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toast } from 'sonner';

import { callUpload } from '@/api/fetcher';
import { selectFileUploadMetadata } from '@/redux/session/selectors';

// Define response type
interface UploadResponse {
  url: string;
  [key: string]: unknown;
}

function useFaqSubmit() {
  const { t } = useTranslation();
  const [error, setError] = useState(null);
  const [response, setResponse] = useState<UploadResponse | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);

  const metadata = useSelector(selectFileUploadMetadata);

  const uploadFileToUrl = useCallback(
    async (file: File, path: string) => {
      setError(null);
      setIsLoading(true);
      try {
        const formData = new FormData();
        formData.append('file', file);

        if (metadata) {
          // metadata contains desk_id
          formData.append('metadata', metadata);
        }

        const resp = await retry(() => callUpload(path, formData), [], {
          retriesMax: 3,
          interval: 1000,
          exponential: true,
          factor: 2,
        });

        if (!resp?.url) {
          throw new Error('File upload failed: no URL returned');
        }

        setResponse(resp);

        return resp;
      } catch (err) {
        setError(err);
        toast.error(t('file_upload.upload_failed'));
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [metadata, t]
  );

  return { uploadFileToUrl, isLoading, error, data: response };
}

export default useFaqSubmit;
